// Mirror of the API-side `DeadlineRule` definition. Kept in sync manually —
// these projects don't share a package, so the structural shape lives in two
// places. If you add a new rule kind, anchor, recurrence, or condition,
// update the API copy as well (creed-api/src/utils/deadlineRules.ts).

// ─── Anchors ──────────────────────────────────────────────────────────────

export type DeadlineAnchor =
  | 'fiscalYearEnd'
  | 'fiscalYearStart'
  | 'formationDate'
  | 'formationAnniversary'
  | 'endOfFormationMonth'
  | 'registrationDate'
  | 'registrationAnniversary'
  | 'calendarYearStart'
  | 'calendarYearEnd'
  | 'quarterEnd'
  | 'taxableEvent'
  | 'solicitationStart'
  | 'incorporationDate'
  | 'irsSubmissionDate'
  | 'irsDeterminationLetterDate';

export const ANCHOR_OPTIONS: Array<{ value: DeadlineAnchor; label: string }> = [
  { value: 'fiscalYearEnd', label: 'Fiscal year end' },
  { value: 'fiscalYearStart', label: 'Fiscal year start' },
  { value: 'formationDate', label: 'Formation date' },
  { value: 'formationAnniversary', label: 'Formation anniversary' },
  { value: 'endOfFormationMonth', label: 'End of formation month' },
  { value: 'registrationDate', label: 'Registration date' },
  { value: 'registrationAnniversary', label: 'Registration anniversary' },
  { value: 'calendarYearStart', label: 'January 1' },
  { value: 'calendarYearEnd', label: 'December 31' },
  { value: 'quarterEnd', label: 'Quarter end' },
  { value: 'taxableEvent', label: 'Taxable event' },
  { value: 'solicitationStart', label: 'Solicitation start' },
  // Formation events. Rules anchored on these describe a one-off initial
  // filing rather than a recurring cycle, so they default to one-time.
  { value: 'incorporationDate', label: 'Formation — incorporation' },
  { value: 'irsSubmissionDate', label: 'Formation — IRS submission' },
  { value: 'irsDeterminationLetterDate', label: 'Formation — IRS letter of determination' },
];

/** Anchors that describe a formation event rather than a recurring cycle. */
export const FORMATION_ANCHORS: DeadlineAnchor[] = [
  'incorporationDate',
  'irsSubmissionDate',
  'irsDeterminationLetterDate',
];

export const ANCHOR_LABELS: Record<DeadlineAnchor, string> = ANCHOR_OPTIONS.reduce(
  (acc, o) => ({ ...acc, [o.value]: o.label }),
  {} as Record<DeadlineAnchor, string>
);

// ─── Recurrence ───────────────────────────────────────────────────────────

export type RecurrenceKind =
  | 'oneTime'
  | 'annual'
  | 'biennial'
  | 'quadrennial'
  | 'quinquennial'
  | 'decennial'
  | 'quarterly'
  | 'monthly'
  | 'asNeeded';

export type DeadlineRecurrence =
  | { kind: 'oneTime' }
  | { kind: 'annual' }
  | { kind: 'biennial'; parity?: 'odd' | 'even' | 'fromFormationYear' }
  | { kind: 'quadrennial' }
  | { kind: 'quinquennial' }
  | { kind: 'decennial' }
  | { kind: 'quarterly' }
  | { kind: 'monthly' }
  | { kind: 'asNeeded' };

export const RECURRENCE_OPTIONS: Array<{ value: RecurrenceKind; label: string }> = [
  { value: 'annual', label: 'Annual' },
  { value: 'biennial', label: 'Biennial (every 2 years)' },
  { value: 'quadrennial', label: 'Quadrennial (every 4 years)' },
  { value: 'quinquennial', label: 'Quinquennial (every 5 years)' },
  { value: 'decennial', label: 'Decennial (every 10 years)' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'oneTime', label: 'One-time' },
  { value: 'asNeeded', label: 'As needed' },
];

export const PARITY_OPTIONS: Array<{ value: 'odd' | 'even' | 'fromFormationYear'; label: string }> = [
  { value: 'odd', label: 'Odd-numbered years' },
  { value: 'even', label: 'Even-numbered years' },
  { value: 'fromFormationYear', label: 'Same parity as formation year' },
];

// ─── Conditions ───────────────────────────────────────────────────────────

export type FoundationStatus = 'publicCharity' | 'privateFoundation';
export type FilingStatus = 'current' | 'pastDue' | 'revoked';
export type FilingType = 'initial' | 'renewal';

export type DeadlineCondition =
  | { kind: 'foundationStatusIs'; status: FoundationStatus }
  | { kind: 'filingStatusIs'; status: FilingStatus | FilingStatus[] }
  | { kind: 'filingTypeIs'; filingType: FilingType }
  | { kind: 'revenueAtLeast'; amount: number }
  | { kind: 'revenueBelow'; amount: number }
  | { kind: 'assetsAtLeast'; amount: number }
  | { kind: 'assetsBelow'; amount: number }
  | { kind: 'entityTypeIn'; entityKeys: string[] }
  | { kind: 'entityTypeNotIn'; entityKeys: string[] }
  | { kind: 'hasEmployees' }
  | { kind: 'noEmployees' }
  | { kind: 'isChurch' }
  | { kind: 'notChurch' }
  | { kind: 'solicitsInJurisdiction' }
  | { kind: 'fiscalYearEndIs'; month: number; day: number }
  | { kind: 'flag'; flag: string };

export type ConditionKind = DeadlineCondition['kind'];

export const CONDITION_OPTIONS: Array<{ value: ConditionKind; label: string }> = [
  { value: 'foundationStatusIs', label: 'Foundation status is …' },
  { value: 'filingStatusIs', label: 'Filing status is …' },
  { value: 'filingTypeIs', label: 'Filing is initial / renewal' },
  { value: 'revenueAtLeast', label: 'Revenue ≥ amount' },
  { value: 'revenueBelow', label: 'Revenue < amount' },
  { value: 'assetsAtLeast', label: 'Assets ≥ amount' },
  { value: 'assetsBelow', label: 'Assets < amount' },
  { value: 'entityTypeIn', label: 'Entity type is one of' },
  { value: 'entityTypeNotIn', label: 'Entity type is NOT one of' },
  { value: 'hasEmployees', label: 'Has employees' },
  { value: 'noEmployees', label: 'No employees' },
  { value: 'isChurch', label: 'Is a church' },
  { value: 'notChurch', label: 'Not a church' },
  { value: 'solicitsInJurisdiction', label: 'Solicits in this jurisdiction' },
  { value: 'fiscalYearEndIs', label: 'Fiscal year end is …' },
  { value: 'flag', label: 'Custom flag …' },
];

export const FOUNDATION_STATUS_OPTIONS: Array<{ value: FoundationStatus; label: string }> = [
  { value: 'publicCharity', label: 'Public charity' },
  { value: 'privateFoundation', label: 'Private foundation' },
];

export const FILING_STATUS_OPTIONS: Array<{ value: FilingStatus; label: string }> = [
  { value: 'current', label: 'Current' },
  { value: 'pastDue', label: 'Past due' },
  { value: 'revoked', label: 'Revoked' },
];

export const FILING_TYPE_OPTIONS: Array<{ value: FilingType; label: string }> = [
  { value: 'initial', label: 'Initial (first-ever filing)' },
  { value: 'renewal', label: 'Renewal' },
];

export function makeEmptyCondition(kind: ConditionKind): DeadlineCondition {
  switch (kind) {
    case 'foundationStatusIs':
      return { kind, status: 'publicCharity' };
    case 'filingStatusIs':
      return { kind, status: ['pastDue'] };
    case 'filingTypeIs':
      return { kind, filingType: 'initial' };
    case 'revenueAtLeast':
    case 'revenueBelow':
    case 'assetsAtLeast':
    case 'assetsBelow':
      return { kind, amount: 0 };
    case 'entityTypeIn':
    case 'entityTypeNotIn':
      return { kind, entityKeys: [] };
    case 'fiscalYearEndIs':
      return { kind, month: 12, day: 31 };
    case 'flag':
      return { kind, flag: '' };
    default:
      return { kind } as DeadlineCondition;
  }
}

const FOUNDATION_STATUS_LABEL: Record<FoundationStatus, string> = {
  publicCharity: 'public charity',
  privateFoundation: 'private foundation',
};

const FILING_STATUS_LABEL: Record<FilingStatus, string> = {
  current: 'current',
  pastDue: 'past due',
  revoked: 'revoked',
};

export function formatCondition(c: DeadlineCondition): string {
  switch (c.kind) {
    case 'foundationStatusIs':
      return `Foundation status is ${FOUNDATION_STATUS_LABEL[c.status]}`;
    case 'filingStatusIs': {
      const list = Array.isArray(c.status) ? c.status : [c.status];
      return `Filing status is ${list.map((x) => FILING_STATUS_LABEL[x]).join(' or ')}`;
    }
    case 'filingTypeIs':
      return c.filingType === 'initial' ? 'Initial filing' : 'Renewal filing';
    case 'revenueAtLeast':
      return `Revenue ≥ $${c.amount.toLocaleString()}`;
    case 'revenueBelow':
      return `Revenue < $${c.amount.toLocaleString()}`;
    case 'assetsAtLeast':
      return `Assets ≥ $${c.amount.toLocaleString()}`;
    case 'assetsBelow':
      return `Assets < $${c.amount.toLocaleString()}`;
    case 'entityTypeIn':
      return `Entity type ∈ {${c.entityKeys.join(', ')}}`;
    case 'entityTypeNotIn':
      return `Entity type ∉ {${c.entityKeys.join(', ')}}`;
    case 'hasEmployees':
      return 'Has employees';
    case 'noEmployees':
      return 'No employees';
    case 'isChurch':
      return 'Is a church';
    case 'notChurch':
      return 'Not a church';
    case 'solicitsInJurisdiction':
      return 'Solicits in this jurisdiction';
    case 'fiscalYearEndIs':
      return `FYE = ${c.month}/${c.day}`;
    case 'flag':
      return `Flag: ${c.flag}`;
  }
}

// ─── Extension ────────────────────────────────────────────────────────────

export interface DeadlineExtension {
  form?: string;
  offset: { months?: number; days?: number; weeks?: number };
  automatic?: boolean;
  description?: string;
}

// ─── Rule kinds ───────────────────────────────────────────────────────────

export type DeadlineRuleKind =
  | 'fixedDate'
  | 'afterFiscalYearEnd'
  | 'afterAnchor'
  | 'nthDayOfMonthAfterFYE'
  | 'nthDayOfMonthAfterAnchor'
  | 'inAnchorMonth'
  | 'wholeAnchorQuarter'
  | 'event';

export type DayOfAnchorMonth = number | 'first' | 'last' | 'anchor';

export type DeadlineEvent =
  | 'priorToSolicitation'
  | 'beforeSoliciting'
  | 'anniversaryOfRegistration'
  | 'uponReceiptOfCharitableAssets'
  | 'biennialRenewal'
  | 'annualRenewal'
  | 'custom';

export interface DeadlineRule {
  kind: DeadlineRuleKind;
  id?: string;
  label?: string;
  description?: string;
  anchor?: DeadlineAnchor;
  month?: number;
  day?: number;
  months?: number;
  days?: number;
  weeks?: number;
  monthOffset?: number;
  dayOfMonth?: number;
  anchorDayOfMonth?: DayOfAnchorMonth;
  anchorMonthOffset?: number;
  anchorQuarterOffset?: number;
  event?: DeadlineEvent;
  conditions?: DeadlineCondition[];
  recurrence?: DeadlineRecurrence;
  /** Single extension. Superseded by `extensions`; still read for old data. */
  extension?: DeadlineExtension;
  /** Chained windows, each measured from the end of the previous one. */
  extensions?: DeadlineExtension[];
  gracePeriodDays?: number;
}

/** Returns a rule's extensions in order, preferring the chained form. */
export function resolveExtensions(rule: DeadlineRule): DeadlineExtension[] {
  if (rule.extensions && rule.extensions.length > 0) return rule.extensions;
  return rule.extension ? [rule.extension] : [];
}

// ─── Record-level structures ──────────────────────────────────────────────

export interface ProcessingTime {
  minDays?: number;
  maxDays?: number;
  businessDays?: boolean;
  notes?: string;
}

export interface FilingWindow {
  opensRules?: DeadlineRule[];
  processingTime?: ProcessingTime;
  notes?: string;
}

export interface FilingStatusRule {
  status: FilingStatus;
  reinstatementForm?: string;
  reinstatementFee?: string;
  penalty?: string;
  backFilingRequired?: boolean;
  backFilingNotes?: string;
  deadlineRules?: DeadlineRule[];
  notes?: string;
}

export type Form990Type = '990' | '990-EZ' | '990-PF';

export const FORM_990_TYPE_OPTIONS: Array<{ value: Form990Type; label: string }> = [
  { value: '990', label: 'Form 990' },
  { value: '990-EZ', label: 'Form 990-EZ' },
  { value: '990-PF', label: 'Form 990-PF' },
];

export interface PartIVRule {
  form: Form990Type;
  line?: string;
  requiredWhen?: string;
}

export type AuditRequirement = 'audit' | 'cpaCompiled' | 'cpaReviewed';

export const AUDIT_REQUIREMENT_OPTIONS: Array<{ value: AuditRequirement; label: string }> = [
  { value: 'audit', label: 'Independent audit' },
  { value: 'cpaCompiled', label: 'CPA-compiled statements' },
  { value: 'cpaReviewed', label: 'CPA-reviewed statements' },
];

export interface AuditThresholdTier {
  requirement: AuditRequirement;
  thresholdRevenue?: number;
  thresholdRevenueMax?: number;
  form?: string;
  agency?: string;
  notes?: string;
}

export const RULE_KIND_OPTIONS: Array<{ value: DeadlineRuleKind; label: string }> = [
  { value: 'fixedDate', label: 'Fixed date (Month + Day)' },
  { value: 'afterFiscalYearEnd', label: 'Duration after fiscal year end' },
  { value: 'afterAnchor', label: 'Duration after anchor' },
  { value: 'nthDayOfMonthAfterFYE', label: 'Nth day of Nth month after FYE' },
  { value: 'nthDayOfMonthAfterAnchor', label: 'Nth day of Nth month after anchor' },
  { value: 'inAnchorMonth', label: 'Day inside anchor month' },
  { value: 'wholeAnchorQuarter', label: 'End of anchor quarter' },
  { value: 'event', label: 'Event-based / qualitative' },
];

export const EVENT_OPTIONS: Array<{ value: DeadlineEvent; label: string }> = [
  { value: 'priorToSolicitation', label: 'Prior to solicitation' },
  { value: 'beforeSoliciting', label: 'Before soliciting' },
  { value: 'anniversaryOfRegistration', label: 'Anniversary date of initial registration' },
  { value: 'uponReceiptOfCharitableAssets', label: 'Upon receipt of charitable assets' },
  { value: 'annualRenewal', label: 'Annual renewal' },
  { value: 'biennialRenewal', label: 'Biennial renewal' },
  { value: 'custom', label: 'Custom (free text)' },
];

const EVENT_LABEL_MAP: Record<DeadlineEvent, string> = EVENT_OPTIONS.reduce(
  (acc, o) => ({ ...acc, [o.value]: o.label }),
  {} as Record<DeadlineEvent, string>
);

export const MONTHS: Array<{ value: number; label: string }> = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

export const ANCHOR_DAY_OPTIONS: Array<{ value: 'first' | 'last' | 'anchor'; label: string }> = [
  { value: 'first', label: 'First day of month' },
  { value: 'last', label: 'Last day of month' },
  { value: 'anchor', label: 'Same day as anchor' },
];

// ─── Defaults ─────────────────────────────────────────────────────────────

export function defaultAnchorForKind(kind: DeadlineRuleKind): DeadlineAnchor | null {
  switch (kind) {
    case 'fixedDate':
      return 'calendarYearStart';
    case 'afterFiscalYearEnd':
    case 'nthDayOfMonthAfterFYE':
      return 'fiscalYearEnd';
    case 'afterAnchor':
    case 'nthDayOfMonthAfterAnchor':
      return 'fiscalYearEnd';
    case 'inAnchorMonth':
    case 'wholeAnchorQuarter':
      return 'formationAnniversary';
    case 'event':
      return null;
  }
}

export function resolveAnchor(rule: DeadlineRule): DeadlineAnchor | null {
  return rule.anchor ?? defaultAnchorForKind(rule.kind);
}

// ─── Formatter ────────────────────────────────────────────────────────────

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] ?? s[v] ?? s[0]);
}

function anchorLabel(anchor: DeadlineAnchor | null | undefined): string {
  if (!anchor) return 'fiscal year end';
  return ANCHOR_LABELS[anchor].toLowerCase();
}

function durationLabel(rule: Pick<DeadlineRule, 'months' | 'days' | 'weeks'>): string {
  const parts: string[] = [];
  if (rule.months != null && rule.months !== 0) {
    parts.push(`${rule.months} month${rule.months === 1 ? '' : 's'}`);
  }
  if (rule.weeks != null && rule.weeks !== 0) {
    parts.push(`${rule.weeks} week${rule.weeks === 1 ? '' : 's'}`);
  }
  if (rule.days != null && rule.days !== 0) {
    parts.push(`${rule.days} day${rule.days === 1 ? '' : 's'}`);
  }
  return parts.length ? parts.join(' and ') : '0 days';
}

function recurrenceLabel(r: DeadlineRecurrence | undefined): string {
  if (!r || r.kind === 'oneTime' || r.kind === 'annual') return '';
  switch (r.kind) {
    case 'biennial':
      return ' (every 2 years)';
    case 'quadrennial':
      return ' (every 4 years)';
    case 'quinquennial':
      return ' (every 5 years)';
    case 'decennial':
      return ' (every 10 years)';
    case 'quarterly':
      return ' (quarterly)';
    case 'monthly':
      return ' (monthly)';
    case 'asNeeded':
      return ' (as needed)';
    default:
      return '';
  }
}

function dayOfAnchorMonthLabel(d: DayOfAnchorMonth | undefined): string {
  if (d === 'first') return 'first day';
  if (d === 'last') return 'last day';
  if (d === 'anchor' || d == null) return 'same day';
  if (typeof d === 'number') return `${ordinal(d)} day`;
  return String(d);
}

export function formatDeadlineRule(rule: DeadlineRule): string {
  const recur = recurrenceLabel(rule.recurrence);
  const base = formatDeadlineRuleCore(rule);
  return rule.label ? `${rule.label}: ${base}${recur}` : `${base}${recur}`;
}

function formatDeadlineRuleCore(rule: DeadlineRule): string {
  switch (rule.kind) {
    case 'fixedDate': {
      const m = MONTHS.find((x) => x.value === rule.month)?.label ?? '?';
      return `${m} ${rule.day}`;
    }
    case 'afterFiscalYearEnd':
      return `${durationLabel(rule)} after FYE`;
    case 'afterAnchor':
      return `${durationLabel(rule)} after ${anchorLabel(resolveAnchor(rule))}`;
    case 'nthDayOfMonthAfterFYE':
      return `${ordinal(rule.dayOfMonth ?? 1)} day of ${ordinal(
        rule.monthOffset ?? 1
      )} month after FYE`;
    case 'nthDayOfMonthAfterAnchor':
      return `${ordinal(rule.dayOfMonth ?? 1)} day of ${ordinal(
        rule.monthOffset ?? 1
      )} month after ${anchorLabel(resolveAnchor(rule))}`;
    case 'inAnchorMonth': {
      const dayLbl = dayOfAnchorMonthLabel(rule.anchorDayOfMonth);
      const offset = rule.anchorMonthOffset ?? 0;
      const offsetLbl = offset === 0 ? '' : ` + ${offset} month${offset === 1 ? '' : 's'}`;
      return `${dayLbl} of ${anchorLabel(resolveAnchor(rule))} month${offsetLbl}`;
    }
    case 'wholeAnchorQuarter':
      return `end of ${anchorLabel(resolveAnchor(rule))} quarter`;
    case 'event':
      if (rule.event === 'custom') return rule.description?.trim() || 'Custom';
      return EVENT_LABEL_MAP[rule.event ?? 'custom'] || rule.description || 'Event';
  }
}

export function formatDeadlineRules(rules: DeadlineRule[] | undefined | null): string {
  if (!rules || rules.length === 0) return '';
  return rules.map(formatDeadlineRule).join(' OR ');
}

/** Construct an empty rule of the given kind with sensible defaults. */
export function makeEmptyRule(kind: DeadlineRuleKind): DeadlineRule {
  switch (kind) {
    case 'fixedDate':
      return { kind: 'fixedDate', month: 1, day: 1 };
    case 'afterFiscalYearEnd':
      return { kind: 'afterFiscalYearEnd' };
    case 'afterAnchor':
      return { kind: 'afterAnchor', anchor: 'fiscalYearEnd' };
    case 'nthDayOfMonthAfterFYE':
      return { kind: 'nthDayOfMonthAfterFYE', monthOffset: 1, dayOfMonth: 1 };
    case 'nthDayOfMonthAfterAnchor':
      return {
        kind: 'nthDayOfMonthAfterAnchor',
        anchor: 'fiscalYearEnd',
        monthOffset: 1,
        dayOfMonth: 1,
      };
    case 'inAnchorMonth':
      return {
        kind: 'inAnchorMonth',
        anchor: 'formationAnniversary',
        anchorDayOfMonth: 'last',
      };
    case 'wholeAnchorQuarter':
      return { kind: 'wholeAnchorQuarter', anchor: 'formationAnniversary' };
    case 'event':
      return { kind: 'event', event: 'priorToSolicitation' };
  }
}

export function ruleSupportsAnchor(kind: DeadlineRuleKind): boolean {
  return kind !== 'fixedDate' && kind !== 'event' && kind !== 'afterFiscalYearEnd' && kind !== 'nthDayOfMonthAfterFYE';
}
